import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ClubProvider } from "@/hooks/useClubContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";
import CustomDomainRedirect from "@/components/public/CustomDomainRedirect";
import NotFound from "./pages/NotFound";
import AutoRegisterInterestPopup from "@/components/AutoRegisterInterestPopup";

// Lazy-loaded route groups
const Index = lazy(() => import("./pages/Index"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Launch = lazy(() => import("./pages/Launch"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const FacebookCallbackPage = lazy(() => import("./pages/auth/FacebookCallbackPage"));

// Dashboard
const DashboardLayout = lazy(() => import("@/components/dashboard/DashboardLayout"));
const DashboardOverview = lazy(() => import("@/pages/dashboard/DashboardOverview"));
const FixturesPage = lazy(() => import("@/pages/dashboard/FixturesPage"));
const ResultsPage = lazy(() => import("@/pages/dashboard/ResultsPage"));
const PlayersPage = lazy(() => import("@/pages/dashboard/PlayersPage"));
const NewsPage = lazy(() => import("@/pages/dashboard/NewsPage"));
const MembersPage = lazy(() => import("@/pages/dashboard/MembersPage"));
const GalleryPage = lazy(() => import("@/pages/dashboard/GalleryPage"));
const VideosPage = lazy(() => import("@/pages/dashboard/VideosPage"));
const HonoursPage = lazy(() => import("@/pages/dashboard/HonoursPage"));
const AnnouncementsPage = lazy(() => import("@/pages/dashboard/AnnouncementsPage"));
const EventsPage = lazy(() => import("@/pages/dashboard/EventsPage"));
const NotificationsPage = lazy(() => import("@/pages/dashboard/NotificationsPage"));
const EmailManagerPage = lazy(() => import("@/pages/dashboard/EmailManagerPage"));
const SettingsPage = lazy(() => import("@/pages/dashboard/SettingsPage"));
const SponsorsPage = lazy(() => import("@/pages/dashboard/SponsorsPage"));
const SupportPage = lazy(() => import("@/pages/dashboard/SupportPage"));
const SocialContentPage = lazy(() => import("@/pages/dashboard/SocialContentPage"));
const SiteEditorPage = lazy(() => import("@/pages/dashboard/SiteEditorPage"));

// Public club pages
const PublicLayout = lazy(() => import("@/components/public/PublicLayout"));
const PublicHome = lazy(() => import("@/pages/public/PublicHome"));
const PublicFixtures = lazy(() => import("@/pages/public/PublicFixtures"));
const PublicResults = lazy(() => import("@/pages/public/PublicResults"));
const PublicResultDetail = lazy(() => import("@/pages/public/PublicResultDetail"));
const PublicMatchLive = lazy(() => import("@/pages/public/PublicMatchLive"));
const PublicPlayers = lazy(() => import("@/pages/public/PublicPlayers"));
const PublicPlayerProfile = lazy(() => import("@/pages/public/PublicPlayerProfile"));
const PublicNews = lazy(() => import("@/pages/public/PublicNews"));
const PublicNewsArticle = lazy(() => import("@/pages/public/PublicNewsArticle"));
const PublicLeagueTable = lazy(() => import("@/pages/public/PublicLeagueTable"));
const PublicHonours = lazy(() => import("@/pages/public/PublicHonours"));
const PublicHistory = lazy(() => import("@/pages/public/PublicHistory"));
const PublicContact = lazy(() => import("@/pages/public/PublicContact"));
const PublicAbout = lazy(() => import("@/pages/public/PublicAbout"));
const PublicGallery = lazy(() => import("@/pages/public/PublicGallery"));
const PublicEvents = lazy(() => import("@/pages/public/PublicEvents"));
const PublicSponsors = lazy(() => import("@/pages/public/PublicSponsors"));
const PublicStats = lazy(() => import("@/pages/public/PublicStats"));
const PublicMembersAuth = lazy(() => import("@/pages/public/PublicMembersAuth"));
const PublicMembersDashboard = lazy(() => import("@/pages/public/PublicMembersDashboard"));
const PublicVideos = lazy(() => import("@/pages/public/PublicVideos"));
const PublicInstall = lazy(() => import("@/pages/public/PublicInstall"));
const PublicShop = lazy(() => import("@/pages/public/PublicShop"));

// Admin
const SuperAdminLayout = lazy(() => import("@/components/admin/SuperAdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminClubs = lazy(() => import("@/pages/admin/AdminClubs"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminFeatureFlags = lazy(() => import("@/pages/admin/AdminFeatureFlags"));
const AdminTickets = lazy(() => import("@/pages/admin/AdminTickets"));
const AdminEmailsPage = lazy(() => import("@/pages/admin/AdminEmailsPage"));
const AdminPayments = lazy(() => import("@/pages/admin/AdminPayments"));
const AdminFunnel = lazy(() => import("@/pages/admin/AdminFunnel"));
const AdminWaitlist = lazy(() => import("@/pages/admin/AdminWaitlist"));
const AdminPlayCricket = lazy(() => import("@/pages/admin/AdminPlayCricket"));
const AdminStripeTest = lazy(() => import("@/pages/admin/AdminStripeTest"));
const AdminCarousels = lazy(() => import("@/pages/admin/AdminCarousels"));
const AdminMemes = lazy(() => import("@/pages/admin/AdminMemes"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const CANONICAL_HOST = "uptheclub.com";
const REDIRECT_HOSTS = new Set(["www.uptheclub.com", "uptheclub.co.uk", "www.uptheclub.co.uk"]);

const CanonicalDomainRedirect = ({ children }: { children: React.ReactNode }) => {
  if (typeof window !== "undefined" && REDIRECT_HOSTS.has(window.location.hostname)) {
    const target = new URL(window.location.href);
    target.protocol = "https:";
    target.hostname = CANONICAL_HOST;
    window.location.replace(target.toString());
    return <Loading />;
  }

  return <>{children}</>;
};

const App = () => (
  <CanonicalDomainRedirect>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ClubProvider>
            <TooltipProvider>
              <ErrorBoundary>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <CustomDomainRedirect>
                    <AutoRegisterInterestPopup />
                    <Suspense fallback={<Loading />}>
                      <Routes>
                      <Route path="/" element={<Launch />} />
                      <Route path="/home" element={<Index />} />
                      <Route path="/pitch" element={<PitchDeck />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/launch" element={<Launch />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                      <Route path="/data-deletion" element={<DataDeletion />} />
                      <Route path="/auth/facebook/callback" element={<FacebookCallbackPage />} />

                      {/* Public club website */}
                      <Route path="/club/:slug" element={<PublicLayout />}>
                        <Route index element={<PublicHome />} />
                        <Route path="fixtures" element={<PublicFixtures />} />
                        <Route path="results" element={<PublicResults />} />
                        <Route path="results/:fixtureId" element={<PublicResultDetail />} />
                        <Route path="cricket/matches/:matchId" element={<PublicMatchLive />} />
                        <Route path="players" element={<PublicPlayers />} />
                        <Route path="players/:playerId" element={<PublicPlayerProfile />} />
                        <Route path="news" element={<PublicNews />} />
                        <Route path="news/:articleId" element={<PublicNewsArticle />} />
                        <Route path="league-table" element={<PublicLeagueTable />} />
                        <Route path="honours" element={<PublicHonours />} />
                        <Route path="history" element={<PublicHistory />} />
                        <Route path="about" element={<PublicAbout />} />
                        <Route path="gallery" element={<PublicGallery />} />
                        <Route path="events" element={<PublicEvents />} />
                        <Route path="sponsors" element={<PublicSponsors />} />
                        <Route path="stats" element={<PublicStats />} />
                        <Route path="contact" element={<PublicContact />} />
                        <Route path="install" element={<PublicInstall />} />
                        <Route path="members/login" element={<PublicMembersAuth />} />
                        <Route path="members" element={<PublicMembersDashboard />} />
                        <Route path="videos" element={<PublicVideos />} />
                        <Route path="shop" element={<PublicShop />} />
                        <Route path="*" element={<NotFound />} />
                      </Route>

                      {/* Admin dashboard */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <DashboardLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<DashboardOverview />} />
                        <Route path="news" element={<NewsPage />} />
                        <Route path="fixtures" element={<FixturesPage />} />
                        <Route path="results" element={<ResultsPage />} />
                        <Route path="players" element={<PlayersPage />} />
                        <Route path="gallery" element={<GalleryPage />} />
                        <Route path="videos" element={<VideosPage />} />
                        <Route path="honours" element={<HonoursPage />} />
                        <Route path="members" element={<MembersPage />} />
                        <Route path="announcements" element={<AnnouncementsPage />} />
                        <Route path="events" element={<EventsPage />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="email" element={<EmailManagerPage />} />
                        <Route path="sponsors" element={<SponsorsPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="site" element={<SiteEditorPage />} />
                        <Route path="social" element={<SocialContentPage />} />
                        <Route path="support" element={<SupportPage />} />
                      </Route>
                      <Route
                        path="/onboarding"
                        element={
                          <ProtectedRoute>
                            <Onboarding />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute>
                            <SuperAdminLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<AdminOverview />} />
                        <Route path="clubs" element={<AdminClubs />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="flags" element={<AdminFeatureFlags />} />
                        <Route path="tickets" element={<AdminTickets />} />
                        <Route path="emails" element={<AdminEmailsPage />} />
                        <Route path="payments" element={<AdminPayments />} />
                        <Route path="funnel" element={<AdminFunnel />} />
                        <Route path="waitlist" element={<AdminWaitlist />} />
                        <Route path="playcricket" element={<AdminPlayCricket />} />
                        <Route path="stripe-test" element={<AdminStripeTest />} />
                        <Route path="carousels" element={<AdminCarousels />} />
                        <Route path="memes" element={<AdminMemes />} />
                      </Route>
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </CustomDomainRedirect>
                </BrowserRouter>
              </ErrorBoundary>
            </TooltipProvider>
          </ClubProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </CanonicalDomainRedirect>
);

export default App;
